/**
 * Welcome Interstitial Logic
 * Shows welcome screen first, then reveals password form on Enter click
 * Animation happens on every page load (no sessionStorage tracking)
 */

(function() {
  'use strict';

  const WELCOME_SELECTOR = '#diverge-welcome';
  const PASSWORD_SELECTOR = '#diverge-password-content';
  const ENTER_BUTTON_SELECTOR = '#diverge-enter';
  const PASSWORD_INPUT_SELECTOR = 'input[type="password"][name="password"], #Password';
  const WELCOME_REVERSE_FLICKER_MS = 1500;
  const CONTENT_FLICKER_IN_MS = 5000;
  const BLACKOUT_PAUSE_MS = 700;
  const POWER_ON_FLICKER_MS = 200;
  const POWER_ON_GLOW_MS = 320;

  let welcomeEl, passwordEl, enterBtn, passwordInput;
  let hasExited = false;
  let flickerActive = false;

  /**
   * Check if animation should be skipped based on URL parameter
   */
  function shouldSkipAnimation() {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('animation') === '0';
  }

  /**
   * Initialize on DOM ready
   * Animation now happens on every page load (no sessionStorage check)
   * UNLESS ?animation=0 is in the URL
   */
  function init() {
    // Try multiple times to find elements (Shopify sections may load async)
    let attempts = 0;
    const maxAttempts = 10;
    
    function tryInit() {
    welcomeEl = document.querySelector(WELCOME_SELECTOR);
    passwordEl = document.querySelector(PASSWORD_SELECTOR);
    enterBtn = document.querySelector(ENTER_BUTTON_SELECTOR);
    passwordInput = document.querySelector(PASSWORD_INPUT_SELECTOR);

      if (!welcomeEl) {
        attempts++;
        if (attempts < maxAttempts) {
          // Retry after a short delay
          setTimeout(tryInit, 100);
          return;
        }
        // Welcome element not found after max attempts
        console.warn('Welcome element not found after', maxAttempts, 'attempts');
      return;
    }

      // Check if this is password page or main page
      const isPasswordPage = !!passwordEl;
      
      if (isPasswordPage) {
        // Password page - hide welcome immediately and show password form
        hideWelcomeImmediate();
        showPasswordImmediate();
      } else {
        // Main page - check if animation should be skipped
        if (shouldSkipAnimation()) {
          // Skip animation - hide welcome immediately
          hideWelcomeImmediate();
          document.body.classList.remove('diverge-lock');
        } else {
          // Show welcome screen with animation
          showWelcome();
          setupEnterButton();
        }
      }
    }
    
    tryInit();
  }

  /**
   * Show welcome screen
   * Works identically on mobile and desktop
   */
  function showWelcome() {
    if (!welcomeEl) {
      console.warn('Welcome element not found');
      return;
    }

    // Ensure welcome is visible first - use requestAnimationFrame for consistent rendering
    requestAnimationFrame(function() {
      welcomeEl.classList.remove('is-hidden', 'is-exiting');
      welcomeEl.style.display = 'flex';
      welcomeEl.style.visibility = 'visible';
      welcomeEl.style.opacity = '1';
      welcomeEl.style.zIndex = '10000';

      // Check if this is password page or main page
      const isPasswordPage = !!passwordEl;
      
      // Lock body scroll and apply blur/scale/opacity to main content (INDEX PAGE ONLY)
      // Do NOT add diverge-lock on password page
      // Use double RAF to ensure styles are applied consistently on mobile
      requestAnimationFrame(function() {
        if (!isPasswordPage) {
          document.body.classList.add('diverge-lock');
        } else {
          // On password page, ensure diverge-lock is NOT present
          document.body.classList.remove('diverge-lock');
        }
      });

      // Set ARIA attributes
      welcomeEl.setAttribute('aria-hidden', 'false');
      
      // Password page only - hide password form initially
      if (passwordEl) {
        passwordEl.setAttribute('aria-hidden', 'true');
        passwordEl.classList.remove('is-visible');
        
        // On password page, ensure letters will be visible
        // Force show letters after a short delay if animation doesn't start
        setTimeout(function() {
          const letters = document.querySelectorAll('.diverge-sign .letter');
          const redLine = document.querySelector('.diverge-red-line');
          
          // Check if any letters are still off
          let needsFallback = false;
          letters.forEach(function(letter) {
            if (!letter.classList.contains('on')) {
              needsFallback = true;
            }
          });
          
          if (needsFallback && letters.length > 0) {
            console.log('Password page: forcing letters visible');
            letters.forEach(function(letter) {
              letter.classList.add('on');
              letter.style.color = '#ffffff';
              letter.style.opacity = '1';
            });
            
            if (redLine) {
              redLine.classList.add('on');
              redLine.style.opacity = '1';
              redLine.style.backgroundColor = '#ff0000';
            }
          }
        }, 500);
      }
    });
  }

  /**
   * Hide welcome immediately (no animation)
   */
  function hideWelcomeImmediate() {
    if (!welcomeEl) return;

    welcomeEl.classList.add('is-hidden');
    welcomeEl.classList.remove('is-exiting');
    welcomeEl.setAttribute('aria-hidden', 'true');
  }

  /**
   * Show password form immediately (no animation)
   */
  function showPasswordImmediate() {
    if (!passwordEl) return;

    passwordEl.classList.add('is-visible');
    passwordEl.setAttribute('aria-hidden', 'false');
    passwordEl.style.transform = '';
    passwordEl.style.opacity = '';

    // Remove lock
    document.body.classList.remove('diverge-lock');

    // Focus password input after a brief delay
    setTimeout(function() {
      if (passwordInput) {
        passwordInput.focus();
      }
    }, 100);
  }

  /**
   * Setup Enter button click handler
   * Works identically on mobile (touch) and desktop (click)
   */
  function setupEnterButton() {
    if (!enterBtn) return;

    // Support both click and touch events for consistent behavior on mobile and desktop
    enterBtn.addEventListener('click', handleEnterClick);
    enterBtn.addEventListener('touchend', function(e) {
      e.preventDefault();
      handleEnterClick(e);
    }, { passive: false });
  }

  /**
   * Handle Enter button click
   */
  function handleEnterClick(e) {
    if (hasExited) return;
    hasExited = true;

    e.preventDefault();

    // Add ?animation=0 to URL without refreshing the page
    const url = new URL(window.location.href);
    url.searchParams.set('animation', '0');
    window.history.pushState({}, '', url);

    // Start exit transition (no sessionStorage tracking)
    exitWelcome();
  }

  /**
   * Exit welcome with reverse flicker, then content flicker-in.
   */
  function exitWelcome() {
    if (!welcomeEl) return;

    const isPasswordPage = !!passwordEl;

    // Password page fallback keeps a simple fade behavior.
    if (isPasswordPage) {
      welcomeEl.classList.add('is-exiting');
      welcomeEl.setAttribute('aria-hidden', 'true');

      if (passwordEl) {
        passwordEl.classList.add('is-visible');
        passwordEl.setAttribute('aria-hidden', 'false');
      }

      setTimeout(function() {
        welcomeEl.classList.add('is-hidden');
        welcomeEl.classList.remove('is-exiting');
        document.body.classList.remove('diverge-lock');

        if (passwordInput) {
          setTimeout(function() {
            passwordInput.focus();
          }, 1600);
        }
      }, 1600);
      return;
    }

    if (enterBtn) {
      enterBtn.disabled = true;
      enterBtn.style.pointerEvents = 'none';
      enterBtn.style.opacity = '0';
    }

    stopIntroFlicker();

    reverseFlickerOutWelcome(function() {
      welcomeEl.classList.add('is-hidden');
      welcomeEl.classList.remove('is-exiting');
      welcomeEl.setAttribute('aria-hidden', 'true');

      setTimeout(function() {
        prepareMainContentForFlickerIn();
        startMainContentFlickerIn();
      }, BLACKOUT_PAUSE_MS);
    });
  }

  function stopIntroFlicker() {
    flickerActive = false;
  }

  function prepareMainContentForFlickerIn() {
    document.body.classList.add('diverge-content-flicker');

    // Hide offscreen targets BEFORE removing diverge-lock so they never flash visible
    const main = document.querySelector('main#MainContent');
    const header = document.querySelector('header');
    const footer = document.querySelector('footer');
    const targets = collectContentFlickerTargets(main, header, footer);
    const targetGroups = splitTargetsByViewport(targets);
    targetGroups.offscreenTargets.forEach(function(target) {
      target.classList.add('diverge-flicker-target');
      target.style.opacity = '0';
      target.style.visibility = 'hidden';
      target.style.pointerEvents = 'none';
    });

    document.body.classList.remove('diverge-lock'); // Allow scroll during product flicker-in
  }

  function reverseFlickerOutWelcome(onComplete) {
    const letters = Array.from(document.querySelectorAll('.diverge-sign .letter'));
    const redLine = document.querySelector('.diverge-red-line');
    const elements = redLine ? letters.concat(redLine) : letters;

    if (elements.length === 0) {
      setTimeout(onComplete, WELCOME_REVERSE_FLICKER_MS);
      return;
    }

    // Random order for a "powering down" effect.
    const ordered = elements.slice().sort(function() {
      return Math.random() - 0.5;
    });
    const total = ordered.length;
    const step = Math.max(30, Math.floor((WELCOME_REVERSE_FLICKER_MS - 300) / total));

    ordered.forEach(function(element, index) {
      setTimeout(function() {
        reverseFlickerElement(element);
      }, index * step);
    });

    setTimeout(onComplete, WELCOME_REVERSE_FLICKER_MS);
  }

  function reverseFlickerElement(element) {
    const isLetter = element.classList.contains('letter');
    const isRedLine = element.classList.contains('diverge-red-line');
    const flickerCount = 2 + Math.floor(Math.random() * 3);
    const flickerSpeed = 40 + Math.random() * 55;
    let current = 0;

    function toggleOn() {
      element.classList.add('on');
      if (isLetter) {
        element.style.color = '#ffffff';
        element.style.opacity = '1';
      } else if (isRedLine) {
        element.style.opacity = '1';
        element.style.backgroundColor = '#ff0000';
      }
    }

    function toggleOff() {
      element.classList.remove('on');
      if (isLetter) {
        element.style.color = '';
        element.style.opacity = '';
      } else if (isRedLine) {
        element.style.opacity = '';
        element.style.backgroundColor = '';
      }
    }

    function flickerOnce() {
      if (current >= flickerCount * 2) {
        toggleOff();
        return;
      }

      if (element.classList.contains('on')) {
        toggleOff();
      } else {
        toggleOn();
      }

      current++;
      setTimeout(flickerOnce, flickerSpeed);
    }

    flickerOnce();
  }

  function startMainContentFlickerIn() {
    const main = document.querySelector('main#MainContent');
    const header = document.querySelector('header');
    const footer = document.querySelector('footer');
    const targets = collectContentFlickerTargets(main, header, footer);

    if (!targets.length) {
      document.body.classList.remove('diverge-content-flicker');
      return;
    }

    const targetGroups = splitTargetsByViewport(targets);
    const viewportTargets = targetGroups.viewportTargets;
    const offscreenTargets = targetGroups.offscreenTargets;
    const revealWindow = Math.min(
      CONTENT_FLICKER_IN_MS - 220,
      Math.max(800, viewportTargets.length * 220)
    );
    const step = revealWindow / Math.max(1, viewportTargets.length);

    // Viewport targets: staggered flicker-in (same as before)
    viewportTargets.forEach(function(target, index) {
      target.classList.add('diverge-flicker-target');
      target.style.opacity = '0';
      target.style.visibility = 'hidden';
      target.style.pointerEvents = 'none';

      setTimeout(function() {
        revealElementWithGlow(target);
      }, Math.min(revealWindow, index * step));
    });

    // Off-screen targets: flicker when they scroll into view (Intersection Observer)
    const io = new IntersectionObserver(
      function(entries) {
        entries.forEach(function(entry) {
          if (!entry.isIntersecting) return;
          const target = entry.target;
          io.unobserve(target);
          target.classList.add('diverge-flicker-target');
          target.style.opacity = '0';
          target.style.visibility = 'hidden';
          target.style.pointerEvents = 'none';
          revealElementWithGlow(target);
        });
      },
      { threshold: 0.1, rootMargin: '80px' }
    );

    offscreenTargets.forEach(function(target) {
      target.classList.add('diverge-flicker-target');
      target.style.opacity = '0';
      target.style.visibility = 'hidden';
      target.style.pointerEvents = 'none';
      io.observe(target);
    });

    // Cleanup after main duration (ensure any unrevealed offscreen targets become visible)
    setTimeout(function() {
      io.disconnect();
      targets.forEach(function(target) {
        target.classList.remove('diverge-flicker-target');
        target.classList.remove('diverge-power-on');
        target.style.opacity = '1';
        target.style.visibility = '';
        target.style.pointerEvents = '';
      });

      document.body.classList.remove('diverge-content-flicker');
    }, CONTENT_FLICKER_IN_MS);
  }

  function collectContentFlickerTargets(main, header, footer) {
    const targets = [];
    const seen = new Set();

    function addTarget(el) {
      if (!el) return;
      if (!(el instanceof HTMLElement)) return;
      if (seen.has(el)) return;
      seen.add(el);
      targets.push(el);
    }

    const selectors = [
      '.dg-header-center',
      '.dg-header-subtitle',
      'a.header__icon--account',
      'a.header__icon--cart',
      '.dg-filter-drawer',
      '.artwork-tile',
      '.gallery-grid__item',
      '.artwork-tile__image-wrapper',
      '.artwork-tile__title',
      'span.dg-chip'
    ];

    selectors.forEach(function(selector) {
      document.querySelectorAll(selector).forEach(function(node) {
        addTarget(node);
      });
    });

    if (!targets.length) {
      addTarget(header);
      if (main) addTarget(main);
      addTarget(footer);
    }

    return targets;
  }

  function splitTargetsByViewport(targets) {
    const viewportTargets = [];
    const offscreenTargets = [];
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    const minY = -viewportHeight * 0.2;
    const maxY = viewportHeight * 1.25;

    targets.forEach(function(target) {
      const rect = target.getBoundingClientRect();
      const inInitialView = rect.bottom > minY && rect.top < maxY;

      if (inInitialView) {
        viewportTargets.push(target);
      } else {
        offscreenTargets.push(target);
      }
    });

    return {
      viewportTargets: viewportTargets,
      offscreenTargets: offscreenTargets
    };
  }

  function revealElementWithGlow(element) {
    element.style.visibility = 'visible';
    element.style.pointerEvents = '';

    // Very brief power flicker: on -> off -> on (200ms total).
    element.style.opacity = '1';
    setTimeout(function() {
      element.style.opacity = '0';
      setTimeout(function() {
        element.style.opacity = '1';
        element.classList.add('diverge-power-on');

        setTimeout(function() {
          element.classList.remove('diverge-power-on');
        }, POWER_ON_GLOW_MS + 30);
      }, 55);
    }, POWER_ON_FLICKER_MS - 55);
  }


  /**
   * Initialize flicker animation for letters and red line
   * Everything starts OFF, then turns on in random order with flickering
   * Works on mobile and desktop
   */
  function initFlicker() {
    const letters = document.querySelectorAll('.diverge-sign .letter');
    const redLine = document.querySelector('.diverge-red-line');
    const isPasswordPage = !!passwordEl;
    flickerActive = true;
    
    if (letters.length === 0) {
      console.warn('No letters found for flicker animation');
      // On password page, retry after a delay
      if (isPasswordPage) {
        setTimeout(function() {
          initFlicker();
        }, 200);
      }
      return;
    }
    
    // Start everything OFF (letters and red line)
    letters.forEach((letter) => {
      letter.classList.remove('on');
    });
    if (redLine) {
      redLine.classList.remove('on');
    }
    
    // Turn everything on in random order with flickering
    // Use a longer delay on password page to ensure DOM is fully ready
    const delay = isPasswordPage ? 300 : 150;
    setTimeout(function() {
      if (!flickerActive) return;
      turnOnRandomOrder();
    }, delay);
    
    // Fallback: If letters don't turn on within 2 seconds, show them immediately
    setTimeout(function() {
      if (!flickerActive) return;
      const checkLetters = document.querySelectorAll('.diverge-sign .letter');
      const checkRedLine = document.querySelector('.diverge-red-line');
      let allOn = true;
      
      checkLetters.forEach(function(letter) {
        if (!letter.classList.contains('on')) {
          allOn = false;
        }
      });
      
      if (checkRedLine && !checkRedLine.classList.contains('on')) {
        allOn = false;
      }
      
      if (!allOn) {
        console.warn('Animation fallback: showing letters immediately');
        checkLetters.forEach(function(letter) {
          letter.classList.add('on');
          letter.style.color = '#ffffff';
          letter.style.opacity = '1';
        });
        if (checkRedLine) {
          checkRedLine.classList.add('on');
          checkRedLine.style.opacity = '1';
          checkRedLine.style.backgroundColor = '#ff0000';
        }
      }
    }, 2000);
    
    /**
     * Turn everything on in random order with flickering
     */
    function turnOnRandomOrder() {
      if (!flickerActive) return;

      // Re-query elements to ensure we have the latest references
      const currentLetters = document.querySelectorAll('.diverge-sign .letter');
      const currentRedLine = document.querySelector('.diverge-red-line');
      
      if (currentLetters.length === 0) {
        console.warn('No letters found in turnOnRandomOrder');
        return;
      }
      
      // Create array with all letters and red line
      const allElements = [...Array.from(currentLetters)];
      if (currentRedLine) {
        allElements.push(currentRedLine);
      }
      
      // Shuffle array for random order
      const shuffled = allElements.sort(() => Math.random() - 0.5);
      
      // Turn on each element with delay and flickering
      shuffled.forEach((element, index) => {
        // FINE-TUNE: Delay between each element turning on (100-300ms)
        const delay = 100 + Math.random() * 200;
        const totalDelay = index * delay;
        
        setTimeout(() => {
          turnOnWithFlicker(element);
        }, totalDelay);
      });
      
      // After everything is on, start the continuous flicker cycles
      const totalElements = shuffled.length;
      const maxDelay = totalElements * (100 + 200); // Max delay for last element
      setTimeout(() => {
        startCycle(); // Start letters cycle
        if (currentRedLine) {
          startRedLineCycle(); // Start red line cycle separately
        }
      }, maxDelay + 500); // Extra buffer before starting cycles
    }
    
    /**
     * Turn on an element (letter or red line) with flickering
     */
    function turnOnWithFlicker(element) {
      if (!flickerActive) return;

      const isLetter = element.classList.contains('letter');
      const isRedLine = element.classList.contains('diverge-red-line');
      
      // FINE-TUNE: Number of flickers before staying on (2-5)
      const flickerCount = 2 + Math.floor(Math.random() * 4);
      let currentFlicker = 0;
      
      // FINE-TUNE: Flicker speed (30-80ms)
      const flickerSpeed = 30 + Math.random() * 50;
      
      function doFlicker() {
        if (!flickerActive) return;

        if (currentFlicker >= flickerCount) {
          // Final turn on
          element.classList.add('on');
          if (isLetter) {
            element.style.transition = 'opacity 0.1s ease-out, text-shadow 0.1s ease-out, color 0.1s ease-out';
            element.style.color = '#ffffff';
            element.style.opacity = '1';
          } else if (isRedLine) {
            element.style.transition = 'opacity 0.1s ease-out, box-shadow 0.1s ease-out';
            element.style.opacity = '1';
            element.style.backgroundColor = '#ff0000';
          }
          return;
        }
        
        const isOn = element.classList.contains('on');
        
        if (isOn) {
          element.classList.remove('on');
          if (isLetter) {
            element.style.transition = 'opacity 0.02s ease-in, text-shadow 0.02s ease-in, color 0.02s ease-in';
            element.style.color = '';
            element.style.opacity = '';
          } else if (isRedLine) {
            element.style.transition = 'opacity 0.02s ease-in, box-shadow 0.02s ease-in';
            element.style.opacity = '';
            element.style.backgroundColor = '';
          }
        } else {
          element.classList.add('on');
          if (isLetter) {
            element.style.transition = 'opacity 0.05s ease-out, text-shadow 0.05s ease-out, color 0.05s ease-out';
            element.style.color = '#ffffff';
            element.style.opacity = '1';
          } else if (isRedLine) {
            element.style.transition = 'opacity 0.05s ease-out, box-shadow 0.05s ease-out';
            element.style.opacity = '1';
            element.style.backgroundColor = '#ff0000';
          }
        }
        
        currentFlicker++;
        setTimeout(doFlicker, flickerSpeed);
      }
      
      doFlicker();
    }
    
    /**
     * Continuous flicker cycle (after initial turn-on)
     */
    function startCycle() {
      if (!flickerActive) return;

      // FINE-TUNE: Number of letters to flicker at once (1-3)
      const numLetters = 1 + Math.floor(Math.random() * 3);
      const allLettersArray = Array.from(letters);
      const selectedLetters = [];
      const availableLetters = [...allLettersArray];
      
      // Randomly pick letters that haven't been picked in this group
      for (let i = 0; i < numLetters && availableLetters.length > 0; i++) {
        const randomIndex = Math.floor(Math.random() * availableLetters.length);
        selectedLetters.push(availableLetters[randomIndex]);
        availableLetters.splice(randomIndex, 1);
  }

      // For each selected letter, pick off state time and jitter amount
      const letterConfigs = selectedLetters.map(letter => ({
        letter: letter,
        // FINE-TUNE: Off duration range (50-400ms)
        offDuration: 50 + Math.random() * 350,
        // FINE-TUNE: Jitter amount (0-10)
        jitterAmount: Math.floor(Math.random() * 11)
      }));
      
      // Calculate on duration upfront (used for both audio and animation)
      // FINE-TUNE: On duration range (450-1950ms)
      const onDuration = 450 + Math.random() * 1500;
      
      // Calculate total flickering duration (jitters + off + on)
      // Jitters: each letter jitters sequentially, jitterSpeed is 30-70ms (avg ~50ms)
      // Off: happens after jitters, parallel for all letters
      // On: happens after all off states complete
      const avgJitterSpeed = 50; // Average jitter speed (30-70ms range)
      const maxJitterTime = Math.max(...letterConfigs.map(c => c.jitterAmount * avgJitterSpeed));
      const maxOffDuration = Math.max(...letterConfigs.map(c => c.offDuration));
      const totalFlickerDuration = maxJitterTime + maxOffDuration + onDuration;
      
      // Track completion of each letter's sequence
      let completedCount = 0;
      const totalLetters = letterConfigs.length;
      
      // Apply jitters to each letter before the off state
      letterConfigs.forEach(config => {
        applyJitters(config.letter, config.jitterAmount, () => {
          // After jitters, apply off state
          turnOff(config.letter, config.offDuration, () => {
            // This letter is done with its sequence
            completedCount++;
            if (completedCount === totalLetters) {
              // All letters are done, now turn all on together
              
              // Turn all letters on
              letters.forEach(letter => {
                letter.classList.add('on');
                letter.style.transition = 'opacity 0.1s ease-out, text-shadow 0.1s ease-out, color 0.1s ease-out';
                letter.style.color = '#ffffff';
                letter.style.opacity = '1';
              });
              
              // After on state is done, repeat with new group
              setTimeout(() => {
                startCycle();
              }, onDuration);
            }
          });
        });
      });
    }
    
    function applyJitters(element, jitterAmount, callback) {
      if (!flickerActive) {
        callback();
        return;
      }

      if (jitterAmount === 0) {
        callback();
        return;
      }
      
      const isLetter = element.classList.contains('letter');
      const isRedLine = element.classList.contains('diverge-red-line');
      
      let currentJitter = 0;
      // FINE-TUNE: Jitter speed range (30-70ms between jitters)
      const jitterSpeed = 30 + Math.random() * 40;
      
      function doJitter() {
        if (currentJitter >= jitterAmount) {
          callback();
          return;
        }
        
        const isOn = element.classList.contains('on');
        
        if (isOn) {
          element.classList.remove('on');
          // FINE-TUNE: Transition speed when turning off
          if (isLetter) {
            element.style.transition = 'opacity 0.02s ease-in, text-shadow 0.02s ease-in, color 0.02s ease-in';
            element.style.color = '';
            element.style.opacity = '';
          } else if (isRedLine) {
            element.style.transition = 'opacity 0.02s ease-in, box-shadow 0.02s ease-in';
            element.style.opacity = '';
            element.style.backgroundColor = '';
          }
        } else {
          element.classList.add('on');
          // FINE-TUNE: Transition speed when turning on
          if (isLetter) {
            element.style.transition = 'opacity 0.05s ease-out, text-shadow 0.05s ease-out, color 0.05s ease-out';
            element.style.color = '#ffffff';
            element.style.opacity = '1';
          } else if (isRedLine) {
            element.style.transition = 'opacity 0.05s ease-out, box-shadow 0.05s ease-out';
            element.style.opacity = '1';
            element.style.backgroundColor = '#ff0000';
          }
        }
        
        currentJitter++;
        setTimeout(doJitter, jitterSpeed);
      }
      
      doJitter();
    }
    
    function turnOff(element, offDuration, callback) {
      if (!flickerActive) {
        callback();
        return;
      }

      const isLetter = element.classList.contains('letter');
      const isRedLine = element.classList.contains('diverge-red-line');
      
      element.classList.remove('on');
      // FINE-TUNE: Transition speed when turning off
      if (isLetter) {
        element.style.transition = 'opacity 0.03s ease-in, text-shadow 0.03s ease-in, color 0.03s ease-in';
        element.style.color = '';
        element.style.opacity = '';
      } else if (isRedLine) {
        element.style.transition = 'opacity 0.03s ease-in, box-shadow 0.03s ease-in';
        element.style.opacity = '';
        element.style.backgroundColor = '';
      }
      setTimeout(callback, offDuration);
    }
    
    /**
     * Separate animation cycle for red line
     * Runs independently from letters
     */
    function startRedLineCycle() {
      if (!flickerActive) return;
      if (!redLine) return;
      
      // FINE-TUNE: Jitter amount for red line (0-8)
      const jitterAmount = Math.floor(Math.random() * 9);
      
      // Apply jitters before turning off
      applyJitters(redLine, jitterAmount, () => {
        // FINE-TUNE: Off duration range for red line (50-400ms)
        const offDuration = 50 + Math.random() * 350;
        
        // Turn off red line
        turnOff(redLine, offDuration, () => {
          // FINE-TUNE: On duration range for red line (450-1950ms)
          const onDuration = 450 + Math.random() * 1500;
          
          // Turn red line back on
          redLine.classList.add('on');
          redLine.style.transition = 'opacity 0.1s ease-out, box-shadow 0.1s ease-out';
          redLine.style.opacity = '1';
          redLine.style.backgroundColor = '#ff0000';
          
          // After on state, repeat cycle
          setTimeout(() => {
            startRedLineCycle();
          }, onDuration);
        });
      });
    }
  }

  // Run when DOM is ready - ensure it runs on password page
  // Works identically on mobile and desktop
  function runInit() {
    // Small delay to ensure all elements are in DOM
    // Use consistent timing for mobile and desktop
    setTimeout(function() {
      init();
      // Only initialize flicker animation on main page, not password page
      // Wait for init to complete before starting flicker
      setTimeout(function() {
        const isPasswordPage = !!document.querySelector(PASSWORD_SELECTOR);
        if (!isPasswordPage) {
          initFlicker();
        }
      }, 0);
    }, 50);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runInit);
  } else {
    // DOM already loaded, but wait a bit for Shopify sections to render
    runInit();
  }
})();

